import { AbstractFormControl } from "./form-control";
import { controlTag } from "./form-control-initializer";
import utils from "@/utils";
import http from "@/http";

export type FormControl = AbstractFormControl<HTMLElement>;

export type FormControlChangeListener = (control: FormControl) => void;

export interface ControlInitializer<E = HTMLElement> {
    matches(el: HTMLElement): boolean;
    initialize(el: E): FormControl;
}
export interface FormInputValues {
    [name: string]: any;
}

export type FormState = FormInputValues;

export interface FormSettings {
    el?: HTMLElement,
    controlSelector: string,
    saveInitialState?: boolean;
}

export type FormChangeListener = (form: Form, control: FormControl) => void;

export class Form {

    static initialize(formEl: HTMLElement) {
        return controlTag.getAll(formEl.querySelectorAll('.ww-field__control'))
            .then(controls => new Form(formEl as HTMLFormElement, controls));
    }

    private disabled = false;
    private savedState: FormState;

    constructor(
        public readonly el?: HTMLFormElement,
        public controls: FormControl[] = [],
        private changeListeners: FormChangeListener[] = [],
    ) { }

    get action(): string {
        return this.el?.hasAttribute('action') ? this.el.getAttribute('action') : null;
    }

    getControl(name: string) {
        for (let control of this.controls) {
            if (control.getName() == name) {
                return control;
            }
        }
        return null;
    }

    getInputValues(includeEmpty = true): FormInputValues {
        let values = {};
        for (let control of this.controls) {
            if (includeEmpty || !control.isEmpty()) {
                values[control.getName()] = control.getValue();
            }
        }
        return values;
    }

    getInputsWhere(filterFn: (control: FormControl) => boolean) {
        let values = {};
        for (let control of this.controls.filter(filterFn)) {
            values[control.getName()] = control.getValue();
        }
        return values;
    }

    setInputValues(values: FormInputValues) {
        for (let control of this.controls) {
            control.setValue(values[control.getName()]);
        }
    }

    getSavedInputValue(inputName: string) {
        return this.getSavedState()[inputName];
    }

    getSavedState() {
        if (this.savedState) {
            return this.savedState;
        } else {
            throw "Form state must first be saved with `saveState` in order to access it later on";
        }
    }

    hasChanges(): boolean {
        const currentState = this.getInputValues();
        return JSON.stringify(this.getSavedState()) !== JSON.stringify(currentState);
    }

    saveState() {
        this.savedState = this.getInputValues();
    }

    resetState(variables: FormInputValues = {}) {
        if (this.savedState) {
            this.setInputValues({...this.savedState, ...variables});
        } else {
            throw "Form state must first be saved with `saveState` in order for `resetState` to work";
        }
    }

    testValidity() {
        let isValid = !this.el.checkValidity || this.el.checkValidity();
        if (!isValid) { // Trigger native form validation
            this.el.reportValidity();
        }
        return isValid;
    }

    warnsOfUnsavedChanges() {
        return 'warn' in this.el.dataset;
    }

    addChangeListener(onChange: FormChangeListener) {
        this.changeListeners.push(onChange);
        if (this.changeListeners.length == 1) {
            this.controls.forEach(control => control.addChangeListener(this.onControlChange))
        }
    }

    private onControlChange: FormControlChangeListener = control => {
        this.changeListeners.forEach(onChange => onChange(this, control));
    }

    /** Overrides the default synchronous form submission with a validity check followed by the specified function */
    onSubmit(submitFn: (e: SubmitEvent) => Promise<any>) {
        const listener = (e: SubmitEvent) => {
            e.preventDefault();
            if (!this.disabled && this.testValidity()) {
                this.guardAgainstDoubleSubmission(submitFn(e));
            }
        }
        this.el.addEventListener('submit', listener);
        return listener;
    }

    clearSubmitListener(listener: (e: SubmitEvent) => void) {
        this.el.removeEventListener('submit', listener);
    }

    private guardAgainstDoubleSubmission(submission: Promise<any>) {
        this.disabled = true;
        let reenable = () => this.disabled = false;
        submission.then(reenable).catch(reenable);
    }

    getFormData(): FormData {
        let data = new FormData();
        this.appendData(data);
        return data;
    }

    appendData(form: FormData) {
        this.controls.forEach(control => control.appendData(form));
    }
}

export interface FormDataRetrievalStrategy {
    getFormData(form: Form, mutation: string): FormData;
}

export class BrowserFormDataRetrival implements FormDataRetrievalStrategy {
    getFormData(form: Form, mutation: string): FormData {
        let data = new FormData(form.el); // Necessary for file uploads
        data.append('namespace', 'ww-form');
        data.append('mutation', mutation);
        return data;
    }
}

/** Preferred method, since it works with custom elements, and has more direct route matching */
export class CustomFormDataRetrieval implements FormDataRetrievalStrategy {
    getFormData(form: Form, mutation: string) {
        let data = new FormData;
        data.append('mutation', mutation); // In case form has multiple submit buttons
        data.append('nonce', http.getNonce());
        form.appendData(data);
        return data;
    }
}

export class FormManager {

    private btnEl?: HTMLButtonElement;
    private btnEls: HTMLButtonElement[] = [];

    constructor(
        private form: Form,
        private map: FormManagerMap,
        private dataRetrieval: FormDataRetrievalStrategy
    ) { }

    addBtn(btnEl: HTMLButtonElement) {
        btnEl.addEventListener('click', this.onBtnClick);
        this.map[btnEl.name] = this;
        this.btnEls.push(btnEl);
    }

    // Safari previously did not support formEl.submitter. This workaround may no longer be necessary.
    private onBtnClick = (e: Event) => { 
        this.btnEl = e.currentTarget as HTMLButtonElement;
    }

    onSubmit = (e: SubmitEvent) => {
        if (e.submitter instanceof HTMLButtonElement) {
            this.btnEl = e.submitter;
        }
        if (this.canSubmit()) {
            return this.submit();
        }
        return Promise.resolve(); // noop
    }

    private canSubmit(): boolean {
        if ('confirm' in this.btnEl.dataset) {
            return confirm(this.btnEl.dataset.confirm);
        }
        return true;
    }

    private submit() {
        this.isRegularSubmission() ? this.submitRegular() : this.submitAsync();
        return Promise.resolve();
    }

    submitRegular() {
        http.sendSynchronousRequest({ method: 'post', action: this.action, target: '_blank', data: this.form.getInputValues() });
    }

    private get action(): string {
        return this.btnEl?.formAction || this.form.action;
    }

    submitAsync() {
        let request = this.newRequest();
        let withSpinner = http.withSpinner(request, document.body, 500);
        let withToast = http.withToastOnError(withSpinner, 'Failed to submit form');
        let withSuccess = withToast.then(r => this.onSuccess(r)); // Order important to prevent blocking of page redirects due to unsaved changes
        return http.withResponseHandler(withSuccess);
    }

    private isRegularSubmission(): boolean {
        return 'regular' in this.btnEl.dataset;
    }

    private newRequest() {
        let endpoint = http.withPageParams(http.resolveUrl(this.form.action));
        let data = this.dataRetrieval.getFormData(this.form, this.btnEl.name); 
        return fetch(endpoint, { method: 'post', body: data }); 
    }

    private onSuccess(response: Response) {
        utils.removeController(document.body, 'spinner');
        this.form.el.dispatchEvent(new CustomEvent('success')); // Needs to happen before any redirects
        this.updateForm();
        return response;
    }

    private updateForm() {
        if ('toggle' in this.btnEl.dataset) {
            this.toggleForm(this.map[this.btnEl.dataset.toggle]);
        }
    }

    private toggleForm(other?: FormManager) {
        this.toggleStatus(other);
        if (other?.form.el.hidden) {
            this.toggleVisibility(other);
        }
    }

    private toggleStatus(other?: FormManager) {
        this.disableForm();
        other?.enableForm();
    }

    private disableForm() {
        this.form.controls.forEach(control => control.disable());
        this.btnEls.forEach(btnEl => btnEl.disabled = true);
    }

    private enableForm() {
        this.form.controls.forEach(control => control.enable());
        this.btnEls.forEach(btnEl => btnEl.disabled = false);
    }

    private toggleVisibility(other?: FormManager) {
        this.form.el.hidden = true;
        other.form.el.hidden = false;
    }
}

export interface FormManagerMap {
    [btnName: string]: FormManager;
}
